<template>
  <div class="simulate-view">
    <!-- Loading -->
    <div v-if="loading" class="state-screen">
      <p class="state-screen__text">Loading simulation&hellip;</p>
    </div>

    <!-- Error / 404 -->
    <div v-else-if="error" class="state-screen">
      <p class="state-screen__text state-screen__text--error">{{ error }}</p>
      <router-link class="state-screen__link link-ink" :to="{ name: 'games' }"
        >&larr; Back to games</router-link
      >
    </div>

    <!-- Content -->
    <template v-else-if="sim">
      <BoxscoreHeroSection :game="heroGame" />

      <!-- ════════════════════════════════════════════════════════════════
           I. PREDICTED SCORE
           ════════════════════════════════════════════════════════════════ -->
      <section class="press-section" aria-label="Predicted Score">
        <div class="press-section__inner">
          <div class="section__header">
            <div class="section-number">I</div>
            <div class="section__titles">
              <p class="eyebrow">Monte Carlo Prediction &mdash; Expected Value</p>
              <h2 class="display-lg">Projected <em>final score.</em></h2>
              <p class="body-lg section__dek">
                Expected runs across
                {{ sim.sim_count.toLocaleString() }}
                simulated games.
                <span v-if="sim.game_time" class="section__dek-meta">
                  First pitch {{ sim.game_time }}.
                </span>
              </p>
            </div>
          </div>

          <hr class="rule rule--double" />

          <div class="score-card paper-card paper-card--stamped">
            <div class="score-card__inner">
              <div class="score-team">
                <span class="score-team__abbr">{{ sim.away_abbr }}</span>
                <span class="score-team__runs numeric">{{ predictedAwayRuns.toFixed(1) }}</span>
                <span class="score-team__label">Away &middot; Expected Runs</span>
              </div>
              <span class="score-divider" aria-hidden="true">&diams;</span>
              <div class="score-team">
                <span class="score-team__abbr">{{ sim.home_abbr }}</span>
                <span class="score-team__runs numeric">{{ predictedHomeRuns.toFixed(1) }}</span>
                <span class="score-team__label">Home &middot; Expected Runs</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Win Probability (already Pressbox-styled) -->
      <WinProbabilityChart
        :simulation="sim"
        :home-label="sim.home_abbr"
        :away-label="sim.away_abbr"
      />

      <!-- Edge vs. the Market (only when lines exist for this game) -->
      <MarketEdgePanel
        v-if="sim.market"
        :market="sim.market"
        :home-abbr="sim.home_abbr"
        :away-abbr="sim.away_abbr"
        section-number="III"
        :game-status="sim.game_status"
        :home-score="sim.home_score"
        :away-score="sim.away_score"
      />

      <!-- ════════════════════════════════════════════════════════════════
           IV. TOTAL RUNS / OVER-UNDER
           ════════════════════════════════════════════════════════════════ -->
      <section class="press-section" aria-label="Total Runs Distribution">
        <div class="press-section__inner">
          <div class="section__header">
            <div class="section-number">IV</div>
            <div class="section__titles">
              <p class="eyebrow">Over / Under &mdash; Total Runs Market</p>
              <h2 class="display-lg">Where the <em>total</em> lands.</h2>
              <p class="body-lg section__dek">
                Full distribution of simulated combined runs, with each half-point line priced
                against our model.
              </p>
            </div>
          </div>

          <hr class="rule rule--double" />

          <div class="totals-layout">
            <!-- Bar chart -->
            <article class="paper-card run-chart-card">
              <header class="card__header">
                <span class="card__n numeric">&Ntilde; 01</span>
                <h3 class="card__title">Run Distribution</h3>
                <span class="card__tag">Probability by total runs</span>
              </header>
              <div class="run-chart" aria-hidden="true">
                <div
                  v-for="(prob, runs) in chartBars"
                  :key="runs"
                  class="run-chart__col"
                  :title="`${runs} runs: ${(prob * 100).toFixed(1)}%`"
                >
                  <div class="run-chart__bar-wrap">
                    <div
                      class="run-chart__bar"
                      :style="{ height: `${(prob / chartMax) * 100}%` }"
                      :class="{ 'run-chart__bar--mode': runs === modeRuns }"
                    ></div>
                  </div>
                  <span class="run-chart__label numeric">{{ runs }}</span>
                </div>
              </div>
            </article>

            <!-- O/U lines -->
            <article class="paper-card ou-card">
              <header class="card__header">
                <span class="card__n numeric">&Ntilde; 02</span>
                <h3 class="card__title">Lines</h3>
                <span class="card__tag">Over / Push / Under</span>
              </header>

              <div class="ou-summary">
                <div class="ou-summary__stat">
                  <span class="ou-summary__val numeric">{{ expectedTotalRuns.toFixed(1) }}</span>
                  <span class="ou-summary__lbl">Expected Total</span>
                </div>
                <div class="ou-summary__stat">
                  <span class="ou-summary__val numeric">{{ modeRuns }}</span>
                  <span class="ou-summary__lbl">Most Likely</span>
                </div>
              </div>

              <div class="ou-table-scroll">
                <table class="ou-table">
                  <thead>
                    <tr>
                      <th scope="col">Line</th>
                      <th scope="col" class="th--num">Over</th>
                      <th scope="col" class="th--num">Push</th>
                      <th scope="col" class="th--num">Under</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="line in ouLines"
                      :key="line"
                      :class="{ 'ou-row--closest': line === closestLine }"
                    >
                      <td class="ou-cell--line numeric">{{ line }}</td>
                      <td class="th--num numeric" :class="overClass(line)">
                        {{ (overProb(line) * 100).toFixed(1) }}%
                      </td>
                      <td class="th--num numeric ou-cell--push">
                        {{ pushProb(line) > 0 ? (pushProb(line) * 100).toFixed(1) + '%' : '—' }}
                      </td>
                      <td class="th--num numeric" :class="underClass(line)">
                        {{ (underProb(line) * 100).toFixed(1) }}%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </article>
          </div>
        </div>
      </section>

      <!-- Away pitching (visiting first) -->
      <ProjectedPitchingTable
        v-if="awayPitchers.length"
        :pitchers="awayPitchers"
        :team-name="sim.away_team"
        team-label="Visiting"
        section-number="V"
        :active-pitcher-id="activePitcher?.player_id ?? null"
        :prop-edges="propEdges"
        @select="selectPitcher"
      />

      <!-- Home pitching -->
      <ProjectedPitchingTable
        v-if="homePitchers.length"
        :pitchers="homePitchers"
        :team-name="sim.home_team"
        team-label="Home"
        section-number="VI"
        :active-pitcher-id="activePitcher?.player_id ?? null"
        :prop-edges="propEdges"
        @select="selectPitcher"
      />

      <!-- Away lineup (visiting first) -->
      <ProjectedBattingTable
        v-if="awayPlayers.length"
        :players="awayPlayers"
        :team-name="sim.away_team"
        team-label="Visiting"
        section-number="VII"
        :active-player-id="activePlayer?.player_id ?? null"
        :prop-edges="propEdges"
        @select="selectPlayer"
      />

      <!-- Home lineup -->
      <ProjectedBattingTable
        v-if="homePlayers.length"
        :players="homePlayers"
        :team-name="sim.home_team"
        team-label="Home"
        section-number="VIII"
        :active-player-id="activePlayer?.player_id ?? null"
        :prop-edges="propEdges"
        @select="selectPlayer"
      />

      <BatterDrawer :player="activePlayer" @close="activePlayer = null" />
      <PitcherDrawer :pitcher="activePitcher" @close="activePitcher = null" />

      <footer class="footer">
        <div class="footer__inner">
          <span class="footer__dot">&diams;</span>
          <span class="footer__brand">HomerunData &middot; Pressbox</span>
          <span class="footer__dot">&diams;</span>
          <span class="footer__copy">&copy; {{ currentYear }}</span>
        </div>
      </footer>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import BoxscoreHeroSection from '../components/boxscore/BoxscoreHeroSection.vue'
import WinProbabilityChart from '../components/boxscore/WinProbabilityChart.vue'
import MarketEdgePanel from '../components/boxscore/MarketEdgePanel.vue'
import ProjectedBattingTable from '../components/boxscore/ProjectedBattingTable.vue'
import ProjectedPitchingTable from '../components/boxscore/ProjectedPitchingTable.vue'
import BatterDrawer from '../components/boxscore/BatterDrawer.vue'
import PitcherDrawer from '../components/boxscore/PitcherDrawer.vue'
import {
  gamesApi,
  type PlayerProjectionResponse,
  type PitcherProjectionResponse,
  type SimulationDetailResponse
} from '../api/games'

const route = useRoute()

const sim = ref<SimulationDetailResponse | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)
const currentYear = computed(() => new Date().getFullYear())

const heroGame = computed(() => {
  if (!sim.value) return undefined
  return {
    home_team: sim.value.home_team,
    home_abbr: sim.value.home_abbr,
    away_team: sim.value.away_team,
    away_abbr: sim.value.away_abbr,
    date: sim.value.game_date
  }
})

const homePlayers = computed(() => sim.value?.players.filter(p => p.team === 'home') ?? [])
const awayPlayers = computed(() => sim.value?.players.filter(p => p.team === 'away') ?? [])

// Per-player prop edges for inline chips in the projection tables.
const propEdges = computed(() => sim.value?.market?.prop_edges ?? [])

const activePlayer = ref<PlayerProjectionResponse | null>(null)
const activePitcher = ref<PitcherProjectionResponse | null>(null)

function selectPlayer(player: PlayerProjectionResponse | null) {
  activePlayer.value = player
  activePitcher.value = null
}

function selectPitcher(pitcher: PitcherProjectionResponse | null) {
  activePitcher.value = pitcher
  activePlayer.value = null
}

function expectedRuns(dist: number[]): number {
  return dist.reduce((sum, prob, runs) => sum + prob * runs, 0)
}

const predictedHomeRuns = computed(() => (sim.value ? expectedRuns(sim.value.home_run_dist) : 0))
const predictedAwayRuns = computed(() => (sim.value ? expectedRuns(sim.value.away_run_dist) : 0))

const homePitchers = computed<PitcherProjectionResponse[]>(() =>
  (sim.value?.pitchers ?? []).filter(p => p.team === 'home')
)
const awayPitchers = computed<PitcherProjectionResponse[]>(() =>
  (sim.value?.pitchers ?? []).filter(p => p.team === 'away')
)

// ── Total runs / O-U ──────────────────────────────────────────────────────
const ouLines = computed(() => {
  const center = expectedTotalRuns.value || 8.5
  // Round center to nearest 0.5
  const snapped = Math.round(center * 2) / 2
  const lines: number[] = []
  for (let l = snapped - 2; l <= snapped + 2; l += 0.5) {
    if (l >= 0.5) lines.push(l)
  }
  return lines
})

const expectedTotalRuns = computed(() => (sim.value ? expectedRuns(sim.value.total_run_dist) : 0))

// Trim trailing near-zero bars; show at least runs 0–18
const chartBars = computed(() => {
  const dist = sim.value?.total_run_dist ?? []
  let last = Math.max(18, dist.length - 1)
  while (last > 18 && (dist[last] ?? 0) < 0.002) last--
  return dist.slice(0, last + 1)
})

const chartMax = computed(() => Math.max(...chartBars.value, 0.001))

const modeRuns = computed(() => {
  const bars = chartBars.value
  return bars.indexOf(Math.max(...bars))
})

const closestLine = computed(() => {
  const exp = expectedTotalRuns.value
  return ouLines.value.reduce((a, b) => (Math.abs(b - exp) < Math.abs(a - exp) ? b : a))
})

function overProb(line: number): number {
  const dist = sim.value?.total_run_dist ?? []
  return dist.reduce((sum, p, runs) => (runs > line ? sum + p : sum), 0)
}

function pushProb(line: number): number {
  if (!Number.isInteger(line)) return 0
  const dist = sim.value?.total_run_dist ?? []
  return dist[line] ?? 0
}

function underProb(line: number): number {
  const dist = sim.value?.total_run_dist ?? []
  return dist.reduce((sum, p, runs) => (runs < line ? sum + p : sum), 0)
}

function overClass(line: number) {
  const p = overProb(line)
  return p > 0.55 ? 'ou-cell--strong-over' : p > 0.5 ? 'ou-cell--lean-over' : ''
}

function underClass(line: number) {
  const p = underProb(line)
  return p > 0.55 ? 'ou-cell--strong-under' : p > 0.5 ? 'ou-cell--lean-under' : ''
}

onMounted(async () => {
  const id = Number(route.params.id)
  if (Number.isNaN(id)) {
    error.value = 'Invalid game ID.'
    loading.value = false
    return
  }
  try {
    sim.value = await gamesApi.getSimulation(id)
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status
    if (status === 404) {
      error.value = 'No simulation has been run for this game yet.'
    } else {
      error.value = 'Failed to load simulation. Please try again.'
    }
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
/* ════════════════════════════════════════════════════════════════════════
   SimulateView — Pressbox aesthetic
   Cream paper · deep navy ink · ox-blood accents · Fraunces + Newsreader
   ════════════════════════════════════════════════════════════════════════ */

.simulate-view {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: transparent;
  color: var(--ink);
  font-family: var(--font-editorial);
  overflow-x: hidden;
}

/* ─── Loading / error states ─────────────────────────────────────────── */
.state-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 60vh;
  gap: 1.5rem;
  padding: 2rem;
  text-align: center;
}

.state-screen__text {
  font-family: var(--font-ui);
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--ink-faint);
}

.state-screen__text--error {
  color: var(--ox);
}

.state-screen__link {
  font-family: var(--font-ui);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.18em;
}

/* ─── Section frame (shared across I / II / III) ─────────────────────── */
.press-section {
  background: transparent;
  border-bottom: var(--rule-thin) solid var(--paper-edge);
}

.press-section__inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 4rem 2rem;
}

.section__header {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2rem;
  align-items: start;
  max-width: 960px;
}

.section__titles .eyebrow {
  margin-bottom: 1rem;
}

.section__titles .display-lg em {
  font-style: italic;
  color: var(--ox);
}

.section__dek {
  margin-top: 1.25rem;
  max-width: 58ch;
}

.section__dek-meta {
  font-family: var(--font-ui);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--ink-faint);
  margin-left: 0.5rem;
}

/* ─── Generic paper-card header (for sub-panels) ─────────────────────── */
.card__header {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: baseline;
  gap: 0.75rem;
  padding: 1rem 1.5rem 0.85rem;
  border-bottom: var(--rule-thin) solid var(--ink);
}

.card__n {
  font-size: 0.7rem;
  color: var(--ink-ghost);
}

.card__title {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 36,
    'wght' 700;
  font-size: 1.35rem;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.card__tag {
  font-family: var(--font-ui);
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--ox);
  white-space: nowrap;
}

.card__scroll {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* ════════════════════════════════════════════════════════════════════════
   I. PREDICTED SCORE
   ════════════════════════════════════════════════════════════════════════ */
.score-card {
  margin-top: 2rem;
  background: var(--paper-deep);
}

.score-card__inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3rem;
  padding: 3rem 2rem;
  text-align: center;
}

.score-team {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.score-team__abbr {
  font-family: var(--font-ui);
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.24em;
  color: var(--ink-medium);
}

.score-team__runs {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 144,
    'wght' 800;
  font-size: clamp(3rem, 8vw, 6rem);
  line-height: 0.95;
  color: var(--ox);
  letter-spacing: -0.02em;
}

.score-team__label {
  font-family: var(--font-ui);
  font-size: 0.62rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--ink-faint);
}

.score-divider {
  font-family: var(--font-display);
  font-size: 1.5rem;
  color: var(--ox);
  opacity: 0.7;
  transform: translateY(-0.4rem);
}

/* ════════════════════════════════════════════════════════════════════════
   II. TOTAL RUNS / OVER-UNDER
   ════════════════════════════════════════════════════════════════════════ */
.totals-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 2rem;
  margin-top: 2rem;
  align-items: start;
}

/* ── Run distribution card ── */
.run-chart-card {
  background: var(--paper);
}

.run-chart {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 200px;
  padding: 1.5rem 1.25rem 1.75rem;
  position: relative;
}

.run-chart::after {
  content: '';
  position: absolute;
  bottom: 1.75rem;
  left: 1.25rem;
  right: 1.25rem;
  height: 1px;
  background: var(--ink);
}

.run-chart__col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  min-width: 0;
}

.run-chart__bar-wrap {
  flex: 1;
  width: 100%;
  display: flex;
  align-items: flex-end;
}

.run-chart__bar {
  width: 100%;
  /* Mix of ink (home) and ox (away) — visually ox-blood by default */
  background: var(--ox);
  opacity: 0.55;
  transition:
    height 0.3s ease,
    opacity 0.2s ease,
    background 0.2s ease;
  min-height: 2px;
}

.run-chart__col:hover .run-chart__bar {
  opacity: 0.85;
}

.run-chart__bar--mode {
  background: var(--gold);
  opacity: 1;
}

.run-chart__label {
  font-family: var(--font-data);
  font-size: 0.62rem;
  color: var(--ink-faint);
  margin-top: 0.4rem;
  line-height: 1;
}

/* ── O/U card ── */
.ou-card {
  background: var(--paper);
}

.ou-summary {
  display: flex;
  gap: 2.5rem;
  padding: 1.25rem 1.5rem;
  border-bottom: var(--rule-thin) dashed var(--paper-edge);
}

.ou-summary__stat {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.ou-summary__val {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 72,
    'wght' 700;
  font-size: 2rem;
  line-height: 1;
  color: var(--ink);
}

.ou-summary__lbl {
  font-family: var(--font-ui);
  font-size: 0.6rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--ink-faint);
}

.ou-table-scroll {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.ou-table {
  width: 100%;
  border-collapse: collapse;
  font-family: var(--font-editorial);
  font-size: 0.92rem;
  color: var(--ink);
}

.ou-table thead {
  background: var(--paper-deep);
  border-bottom: var(--rule-med) solid var(--ink);
}

.ou-table th {
  padding: 0.65rem 0.9rem;
  font-family: var(--font-ui);
  font-size: 0.6rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--ink-medium);
  text-align: left;
  white-space: nowrap;
}

.ou-table th.th--num {
  text-align: right;
}

.ou-table td {
  padding: 0.6rem 0.9rem;
  border-bottom: var(--rule-thin) dashed var(--paper-edge);
  vertical-align: baseline;
}

.ou-table td.th--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.ou-table tbody tr:hover td {
  background: var(--paper-deep);
}

.ou-cell--line {
  font-variant-numeric: tabular-nums;
  color: var(--ink-medium);
}

.ou-cell--push {
  color: var(--ink-ghost);
  font-size: 0.88em;
}

/* Closest line — ox-blood left border emphasis */
.ou-row--closest td {
  background: var(--ox-10);
  color: var(--ink);
  font-weight: 600;
}

.ou-row--closest td:first-child {
  border-left: var(--rule-thick) solid var(--ox);
  padding-left: calc(0.9rem - var(--rule-thick));
  color: var(--ox);
}

.ou-row--closest:hover td {
  background: var(--ox-10);
}

/* Ivy (+EV) / bruise (-EV) leanings */
.ou-cell--strong-over,
.ou-cell--strong-under {
  color: var(--ivy);
  font-weight: 700;
}

.ou-cell--lean-over,
.ou-cell--lean-under {
  color: var(--ivy);
  opacity: 0.82;
}

/* ════════════════════════════════════════════════════════════════════════
   III. PITCHING PROJECTIONS
   ════════════════════════════════════════════════════════════════════════ */
.pitching-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 2rem;
}

.pitching-card {
  background: var(--paper);
}

.pitching-table {
  width: 100%;
  min-width: 460px;
  border-collapse: collapse;
  background: var(--paper);
  font-family: var(--font-editorial);
  font-size: 0.92rem;
  color: var(--ink);
}

.pitching-table thead {
  background: var(--paper-deep);
  border-bottom: var(--rule-med) solid var(--ink);
}

.pitching-table th {
  padding: 0.6rem 0.65rem;
  font-family: var(--font-ui);
  font-size: 0.6rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--ink-medium);
  text-align: left;
  white-space: nowrap;
}

.pitching-table th.th--num {
  text-align: right;
}

.pitching-table td {
  padding: 0.65rem 0.65rem;
  border-bottom: var(--rule-thin) dashed var(--paper-edge);
  vertical-align: baseline;
  line-height: 1.35;
}

.pitching-table tbody tr:last-child td {
  border-bottom: none;
}

.pitching-table tbody tr:hover td {
  background: var(--paper-deep);
}

.td--player {
  white-space: nowrap;
}

.td--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}

.player-name {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 18,
    'wght' 600;
  font-size: 1rem;
  color: var(--ink);
}

.starter-eyebrow {
  display: block;
  font-family: var(--font-ui);
  font-size: 0.58rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--ox);
  margin-bottom: 0.15rem;
}

.row--starter td {
  background: var(--ox-10);
}

.row--starter:hover td {
  background: var(--ox-10);
}

.badge-sp {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.12rem 0.45rem;
  font-family: var(--font-data);
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: var(--paper);
  background: var(--ox);
  vertical-align: middle;
}

/* ─── Footer ─────────────────────────────────────────────────────────── */
.footer {
  margin-top: auto;
  padding: 2rem 1.5rem;
  border-top: var(--rule-thin) solid var(--ink);
  background: transparent;
}

.footer__inner {
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  font-family: var(--font-ui);
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--ink-faint);
}

.footer__dot {
  color: var(--ox);
}

/* ─── Responsive ─────────────────────────────────────────────────────── */
@media (max-width: 960px) {
  .totals-layout {
    grid-template-columns: 1fr;
  }

  .pitching-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 700px) {
  .press-section__inner {
    padding: 3rem 1rem;
  }

  .section__header {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .section-number {
    width: 2.75rem;
    height: 2.75rem;
    font-size: 1.15rem;
  }

  .score-card__inner {
    flex-direction: row;
    gap: 1rem;
    padding: 2rem 1rem;
  }

  .card__header {
    padding: 0.85rem 1rem 0.75rem;
    grid-template-columns: auto 1fr;
  }

  .card__tag {
    display: none;
  }

  .run-chart {
    height: 160px;
    padding: 1rem 0.75rem 1.5rem;
    gap: 2px;
  }

  .run-chart::after {
    left: 0.75rem;
    right: 0.75rem;
  }

  .ou-summary {
    gap: 1.5rem;
    padding: 1rem;
  }

  .ou-table th,
  .ou-table td {
    padding: 0.55rem 0.6rem;
  }

  .pitching-table {
    font-size: 0.85rem;
  }

  .pitching-table th,
  .pitching-table td {
    padding: 0.5rem 0.5rem;
  }

  .footer__inner {
    flex-wrap: wrap;
    font-size: 0.6rem;
  }
}

@media (max-width: 380px) {
  .press-section__inner {
    padding: 2.5rem 0.85rem;
  }

  .score-card__inner {
    gap: 0.5rem;
    padding: 1.5rem 0.5rem;
  }

  .score-divider {
    font-size: 1.15rem;
  }

  .ou-summary {
    gap: 1rem;
  }

  .ou-summary__val {
    font-size: 1.65rem;
  }
}
</style>
